import { Suspense, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useModalContext } from './ModalContext'
import { modalComponents, modalDefaults } from './ModalRegistry'
import styles from './ModalOutlet.module.scss'

// Cache for loaded components
const componentCache = new Map()

function getModalComponent(modalType) {
    if (componentCache.has(modalType)) {
        return componentCache.get(modalType)
    }

    const LazyComponent = modalComponents[modalType]
    if (!LazyComponent) {
        console.warn(`No component registered for modal: ${modalType}`)
        return null
    }

    componentCache.set(modalType, LazyComponent)
    return LazyComponent
}

/**
 * Error fallback component for modal errors
 */
function ModalErrorFallback({ resetErrorBoundary, onCancel }) {
    return (
        <div className={styles.errorFallback}>
            <p>Something went wrong loading this dialog.</p>
            <div className={styles.errorActions}>
                <button className={styles.cancelButton} onClick={onCancel}>
                    Cancel
                </button>
                <button className={styles.retryButton} onClick={resetErrorBoundary}>
                    Try again
                </button>
            </div>
        </div>
    )
}

/**
 * Renders all open modals
 * Place this once at the app root level (inside ModalProvider)
 */
export function ModalOutlet() {
    const { modals, hide } = useModalContext()

    const openModals = useMemo(() => {
        return Object.entries(modals)
            .filter(([, modal]) => modal.isOpen)
            .map(([instanceId, modal], index) => ({
                instanceId,
                modalType: modal.modalType,
                props: modal.props,
                index,
            }))
    }, [modals])

    const handleError = (error, info) => {
        console.error('Modal Error:', error, info)
    }

    if (openModals.length === 0) return null

    return (
        <>
            {openModals.map(({ instanceId, modalType, props, index }) => {
                const ModalComponent = getModalComponent(modalType)
                if (!ModalComponent) return null

                const defaults = modalDefaults[modalType] || {}
                const zIndex = 1600 + index * 10 // Stack modals
                const handleCancel = () => hide(instanceId, false)

                const {
                    onClose: propsOnClose,
                    onConfirm: propsOnConfirm,
                    onCancel: propsOnCancel,
                    ...restProps
                } = props

                return (
                    <ErrorBoundary
                        key={instanceId}
                        FallbackComponent={(fallbackProps) => (
                            <ModalErrorFallback {...fallbackProps} onCancel={handleCancel} />
                        )}
                        onError={handleError}
                    >
                        <Suspense fallback={null}>
                            <ModalComponent
                                open={true}
                                onClose={(result) => {
                                    propsOnClose?.(result)
                                    hide(instanceId, result)
                                }}
                                onConfirm={(result) => {
                                    propsOnConfirm?.(result)
                                    hide(instanceId, result)
                                }}
                                onCancel={() => {
                                    propsOnCancel?.()
                                    handleCancel()
                                }}
                                zIndex={zIndex}
                                {...defaults}
                                {...restProps}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )
            })}
        </>
    )
}
